// client/pages/admin/FiatPayouts.tsx
// KX-22 (model A) — admin fiat payouts. Completing a payout posts the idempotent
// ledger debit on the backend; the admin only reviews and flips the status.
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Wallet, Loader2, AlertCircle, RefreshCw, CheckCircle2, XCircle } from "lucide-react";
import {
  listFiatPayouts, updateFiatPayoutStatus, AdminPayout,
} from "../../api/services/adminFiatPayoutService";

const fld =
  "rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500";

const statusColor: Record<string, string> = {
  pending: "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300",
  approved: "bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300",
  processing: "bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300",
  completed: "bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-300",
  rejected: "bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300",
};

export default function AdminFiatPayouts() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<AdminPayout[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [status, setStatus] = useState("pending");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const { payouts } = await listFiatPayouts({ status: status === "all" ? undefined : status, limit: 50 });
      setRows(payouts);
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || t("admin.fiatPayouts.errors.load", { defaultValue: "Failed to load payouts" }));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const act = async (id: string, s: "completed" | "rejected") => {
    setBusy(id);
    setErr("");
    try {
      let opts: { rejectionReason?: string } | undefined;
      if (s === "rejected") {
        const reason = window.prompt(t("admin.fiatPayouts.rejectReason", { defaultValue: "Reason for rejection?" }) || "") || undefined;
        opts = { rejectionReason: reason };
      }
      const r = await updateFiatPayoutStatus(id, s, opts);
      if (!r || r.success === false) {
        setErr(r?.message || t("admin.fiatPayouts.errors.action", { defaultValue: "Action failed" }));
        return;
      }
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || t("admin.fiatPayouts.errors.action", { defaultValue: "Action failed" }));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Wallet className="w-6 h-6 text-blue-600" />
          <h1 className="text-xl sm:text-2xl font-bold">{t("admin.fiatPayouts.title", { defaultValue: "Payouts" })}</h1>
        </div>
        <div className="flex items-center gap-2">
          <select className={fld} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="pending">{t("admin.fiatPayouts.status.pending", { defaultValue: "Pending" })}</option>
            <option value="completed">{t("admin.fiatPayouts.status.completed", { defaultValue: "Completed" })}</option>
            <option value="rejected">{t("admin.fiatPayouts.status.rejected", { defaultValue: "Rejected" })}</option>
            <option value="all">{t("admin.fiatPayouts.status.all", { defaultValue: "All" })}</option>
          </select>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      <p className="text-sm text-gray-500 mb-6">
        {t("admin.fiatPayouts.description", {
          defaultValue: "Completing a payout debits the operator's ledger balance (idempotent) — the ledger is the source of truth.",
        })}
      </p>

      {err && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400 px-4 py-3 text-sm">
          <AlertCircle className="w-4 h-4" /> {err}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
      ) : rows.length === 0 ? (
        <div className="text-center py-16 text-gray-500">{t("admin.fiatPayouts.empty", { defaultValue: "No payouts." })}</div>
      ) : (
        <Card className="p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200 dark:border-gray-700">
                <th className="py-3 px-4">{t("admin.fiatPayouts.table.operator", { defaultValue: "Operator" })}</th>
                <th className="py-3 px-4">{t("admin.fiatPayouts.table.amount", { defaultValue: "Amount" })}</th>
                <th className="py-3 px-4">{t("admin.fiatPayouts.table.status", { defaultValue: "Status" })}</th>
                <th className="py-3 px-4 text-right">{t("admin.fiatPayouts.table.actions", { defaultValue: "Actions" })}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-b border-gray-100 dark:border-gray-800 last:border-0">
                  <td className="py-3 px-4">
                    <div className="font-medium">{p.user?.name || "—"}</div>
                    <div className="text-xs text-gray-500">{p.user?.email}</div>
                  </td>
                  <td className="py-3 px-4 font-mono">{p.currency} {p.amount}</td>
                  <td className="py-3 px-4">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${statusColor[p.status] || ""}`}>{p.status}</span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-end gap-2">
                      {p.status === "pending" && (
                        <>
                          <button
                            onClick={() => act(p.id, "completed")}
                            disabled={busy === p.id}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 text-xs font-medium disabled:opacity-50"
                          >
                            {busy === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                            {t("admin.fiatPayouts.complete", { defaultValue: "Complete" })}
                          </button>
                          <button
                            onClick={() => act(p.id, "rejected")}
                            disabled={busy === p.id}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                          >
                            <XCircle className="w-3.5 h-3.5" /> {t("admin.fiatPayouts.reject", { defaultValue: "Reject" })}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
